/**
 * Keeps credentials and other sensitive query data out of PostHog (KAN-253).
 *
 * INTERNAL seam of `./index`, the only module allowed to touch posthog-js; it is
 * pure and imports nothing from posthog so it can be tested exhaustively.
 *
 * The policy is deliberately blunt: **no URL leaves the browser with a query
 * string or a fragment.** Concierge links carry a single-use credential in
 * `/enter?token=...`, and a denylist of parameter names can only ever be as good
 * as the last time somebody remembered to extend it. Pathnames are what funnels
 * and path analysis need; anything the query carried that analytics wants (a
 * search term) already travels as an explicit event property.
 */

/** Property of a `$heatmap` event whose keys are page URLs. */
const HEATMAP_DATA_KEY = "$heatmap_data";

/**
 * Matches an absolute http(s) URL embedded anywhere in a string.
 *
 * `<masked>` is spelled out as an allowed run because posthog writes it into a
 * URL where it masks a parameter's value, and `<` is otherwise a delimiter: left
 * out, the match stopped at it and everything after - the *next* parameter, with
 * its value - was left in the string (KAN-262).
 */
const EMBEDDED_URL = /https?:\/\/(?:<masked>|[^\s"'<>()\\])+/g;

/**
 * Query parameter names masked at the source by posthog's own
 * `custom_personal_data_properties` (see `initAnalytics`).
 *
 * This is the second layer, not the first: `before_send` scrubs every event, but
 * posthog also posts its stored initial person properties
 * (`$initial_current_url`, `$initial_referrer`) to `/flags` with no hook in
 * between, so a credential has to be masked in the stored copy too. Anything
 * not listed here still never reaches an event; it is only this one request that
 * a name outside the list could slip into.
 */
export const SENSITIVE_QUERY_PARAMS = [
  "token",
  // The concierge operator's capability token (KAN-262). `/enter` no longer
  // carries it by the time posthog runs (see `lib/concierge/link-credentials`);
  // listed anyway so a link that arrives some other way is still masked.
  "cap",
  "access_token",
  "id_token",
  "refresh_token",
  "code",
  "password",
  "secret",
  "api_key",
  "key",
  "auth",
  "session",
  "email"
];

/**
 * Reduce a URL to origin + pathname. A string that is not an absolute http(s)
 * URL is returned untouched, except that anything after the first `?` or `#` of
 * a root-relative path is dropped.
 */
export function sanitiseUrl(value: string): string {
  try {
    const url = new URL(value);
    if (url.protocol !== "http:" && url.protocol !== "https:") return value;
    return url.origin + url.pathname;
  } catch {
    if (value.startsWith("/")) return value.split(/[?#]/, 1)[0];
    return value;
  }
}

/**
 * Sanitise every absolute URL embedded in free text (messages, stacks).
 * Exported for `lib/sentry`, which applies the same no-query-string policy to
 * the errors it reports.
 */
export function scrubText(value: string): string {
  return value.replace(EMBEDDED_URL, (match) => sanitiseUrl(match));
}

function scrub(value: unknown, ancestors: WeakSet<object>): unknown {
  if (typeof value === "string") return scrubText(value);
  if (value === null || typeof value !== "object") return value;
  if (value instanceof Date) return value;
  // Cycles cannot occur in a payload posthog is about to serialise, but a
  // scrubber that can recurse forever is not worth the risk. Only the current
  // chain of ancestors is tracked: an object legitimately referenced from two
  // places must be scrubbed in both, never returned raw the second time.
  if (ancestors.has(value)) return "[circular]";
  ancestors.add(value);
  try {
    if (Array.isArray(value))
      return value.map((item) => scrub(item, ancestors));

    const out: Record<string, unknown> = {};
    for (const [key, inner] of Object.entries(value)) {
      if (key === HEATMAP_DATA_KEY) {
        out[key] = scrubHeatmapData(inner, ancestors);
      } else {
        out[scrubText(key)] = scrub(inner, ancestors);
      }
    }
    return out;
  } finally {
    ancestors.delete(value);
  }
}

/**
 * `$$heatmap` payloads are keyed by the page URL, so the URL is a property
 * *name* there. Sanitising keys can make two pages collide (same path, different
 * query); their click lists are concatenated rather than one overwriting the
 * other.
 */
function scrubHeatmapData(data: unknown, ancestors: WeakSet<object>): unknown {
  if (data === null || typeof data !== "object" || Array.isArray(data)) {
    return scrub(data, ancestors);
  }
  const out: Record<string, unknown[]> = {};
  for (const [url, entries] of Object.entries(data)) {
    const key = sanitiseUrl(url);
    const clean = scrub(entries, ancestors);
    out[key] = [
      ...(out[key] ?? []),
      ...(Array.isArray(clean) ? clean : [clean])
    ];
  }
  return out;
}

/** The slice of posthog's `CaptureResult` this module reads and rewrites. */
export interface SanitisableEvent {
  event: string;
  properties?: Record<string, unknown>;
  $set?: Record<string, unknown>;
  $set_once?: Record<string, unknown>;
}

/**
 * posthog `before_send` hook: scrub URLs out of every property of an outgoing
 * event, including the person properties (`$set`, `$set_once`).
 *
 * `before_send` runs after posthog has assembled the full payload - event
 * properties, super properties, session properties and the initial person
 * properties it folds into `$set_once` - so unlike `sanitize_properties` (which
 * is deprecated and never sees the finished event) one hook covers all of it,
 * including `$exception`, `$web_vitals`, `$dead_click` and `$$heatmap`.
 *
 * `$snapshot` (session replay) is skipped: its payload is rrweb data, often
 * gzip-compressed before it reaches here, so it cannot be scrubbed at this
 * point. Replay URLs are masked by `session_recording.maskCapturedNetworkRequestFn`.
 */
export function sanitiseEvent<T extends SanitisableEvent | null>(event: T): T {
  // posthog types the hook's argument as nullable (a chained hook may have
  // dropped the event); there is nothing to scrub in that case.
  if (event === null || event.event === "$snapshot") return event;

  const ancestors = new WeakSet<object>();
  const out: Record<string, unknown> = { ...event };

  for (const field of ["properties", "$set", "$set_once"] as const) {
    if (event[field]) out[field] = scrub(event[field], ancestors);
  }

  return out as T;
}
